import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  NOTIFICATION_CENTER_DEADLINES_TAB_ID,
  NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID,
  NOTIFICATION_CENTER_PAGE_SIZE,
  NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID,
  NOTIFICATION_CENTER_REPORTING_TAB_ID,
  NOTIFICATION_CENTER_TAB_KEYS,
  isNotificationCenterPaginatedTable,
} from "./notificationCenterConstants";
import {
  getNotificationCenterCounters,
  getNotificationCenterTable,
  markNotificationCenterIntermediaryPaymentsRead,
} from "./notificationCenterApi";
import {
  getNotificationCenterShowAllQueryParams,
  resolveNotificationCenterShowAllForApi,
} from "../../../containers/NotificationCenter/utils/notificationCenterAccess";
import {
  NOTIFICATION_CENTER_DEADLINES_SCOPE,
  NOTIFICATION_CENTER_DECLARATION_TYPE,
  NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER,
  NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER,
  NOTIFICATION_CENTER_VISIBILITY,
} from "../../../containers/NotificationCenter/utils/notificationCenterFilterConstants";
import {
  applyStoredNotificationCenterFiltersToState,
  loadNotificationCenterFiltersFromStorage,
  saveNotificationCenterFiltersToStorage,
} from "../../../containers/NotificationCenter/utils/notificationCenterFiltersStorage";
import {
  collectNotificationCenterRegistrationsFilterOptions,
  createEmptyRegistrationsFilterOptions,
  getNotificationCenterRegistrationsDefaultStatuses,
  getNotificationCenterRegistrationsFilterSignature,
  getNotificationCenterRegistrationsQueryParams,
  normalizeRegistrationsCompanyFilter,
  normalizeRegistrationsDateFilter,
  normalizeRegistrationsJurisdictionFilter,
  normalizeRegistrationsMultiSelectFilter,
  normalizeRegistrationsOrderDateFilter,
  normalizeRegistrationsOrdering,
  normalizeRegistrationsTypeFilter,
} from "../../../containers/NotificationCenter/utils/notificationCenterRegistrationsFilters";
import { getNotificationCenterRegistrationTrackerId } from "../../../containers/NotificationCenter/utils/notificationCenterRegistrationHighlight";
import { updateRegistrationTrackerHighlighted } from "../registration-tracker/registrationTrackerSlice";
import { login } from "../auth/authSlice";
function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (value?.items && Array.isArray(value.items)) return value.items;
  if (value?.data && Array.isArray(value.data)) return value.data;
  if (value?.results && Array.isArray(value.results)) return value.results;
  return [];
}

function createEmptyByTab() {
  return {};
}

function createEmptyByTableCounts() {
  return {};
}

function createEmptyTabTableState(limit = NOTIFICATION_CENTER_PAGE_SIZE) {
  return {
    loading: false,
    loaded: false,
    error: null,
    pendingRequestIds: [],
    supersededRequestIds: [],
    fetchedRequestId: null,
    default: {
      items: [],
      total: 0,
      offset: 0,
      limit,
    },
  };
}

function createEmptyIntermediaryPaymentsCounters() {
  return {
    unreadCount: 0,
    actionableCount: 0,
    urgentCount: 0,
  };
}

/**
 * API shape: { total: number, by_table: { reporting?: number, ... } }
 */
export function normalizeNotificationCenterCounters(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      total: 0,
      byTable: createEmptyByTableCounts(),
      allowedTableIds: [],
      intermediaryPayments: createEmptyIntermediaryPaymentsCounters(),
    };
  }

  const source =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const incoming = source.by_table ?? source.byTable ?? {};
  const knownTableIds = Object.keys(incoming).filter((tabId) =>
    NOTIFICATION_CENTER_TAB_KEYS.includes(tabId)
  );
  const byTable = createEmptyByTableCounts();
  for (const tabId of knownTableIds) {
    byTable[tabId] = Number(incoming[tabId]) || 0;
  }
  const allowedTableIds = knownTableIds.filter((tabId) => byTable[tabId] > 0);

  const ipBlock = source.intermediary_payments ?? source.intermediaryPayments ?? {};
  const intermediaryPayments = {
    unreadCount: Number(ipBlock.unread_count ?? ipBlock.unreadCount) || 0,
    actionableCount:
      Number(ipBlock.actionable_count ?? ipBlock.actionableCount) || 0,
    urgentCount: Number(ipBlock.urgent_count ?? ipBlock.urgentCount) || 0,
  };

  return {
    total: Number(source.total) || 0,
    byTable,
    allowedTableIds,
    intermediaryPayments,
  };
}

/**
 * GET /notify/notification_center/:table_id
 * { table_id, total, offset, limit, items: Row[] }
 */
export function normalizeNotificationCenterTableResponse(raw, tableId, fallbackOffset, fallbackLimit) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const parsedOffset = Number(source?.offset);
  const parsedLimit = Number(source?.limit);

  return {
    tableId: source?.table_id ?? tableId,
    items: normalizeList(source?.items),
    total: Number(source?.total) || 0,
    offset: Number.isFinite(parsedOffset) ? parsedOffset : fallbackOffset,
    limit: Number.isFinite(parsedLimit) ? parsedLimit : fallbackLimit,
  };
}

function isSilentFetch(arg) {
  return Boolean(arg && typeof arg === "object" && arg.silent);
}

function resolveDeclarationTypeFilter(declarationType, stateFilter) {
  const allowed = Object.values(NOTIFICATION_CENTER_DECLARATION_TYPE);
  if (allowed.includes(declarationType)) {
    return declarationType;
  }
  if (allowed.includes(stateFilter)) {
    return stateFilter;
  }
  return NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
}

/**
 * @param {string} declarationType
 * @returns {Record<string, string>}
 */
export function getNotificationCenterDeclarationTypeQueryParams(declarationType) {
  const resolved = resolveDeclarationTypeFilter(
    declarationType,
    NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
  );
  if (resolved === NOTIFICATION_CENTER_DECLARATION_TYPE.ALL) {
    return {};
  }
  return { declaration_type: resolved };
}

function tableRequestKey(
  tableId,
  offset,
  limit,
  showAll = false,
  declarationType = NOTIFICATION_CENTER_DECLARATION_TYPE.ALL,
  registrationsFilterSignature = "none",
  intermediaryPaymentsFilterSignature = "none"
) {
  const filter =
    tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
      ? resolveDeclarationTypeFilter(
          declarationType,
          NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
        )
      : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
  const registrations =
    tableId === NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID
      ? registrationsFilterSignature
      : "none";
  const intermediaryPayments =
    tableId === NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID
      ? intermediaryPaymentsFilterSignature
      : "none";
  return `${tableId}:${offset}:${limit}:${showAll ? 1 : 0}:${filter}:${registrations}:${intermediaryPayments}`;
}

/** Registrations filters live in the slice; requests always read the current state. */
function resolveRegistrationsFilters(tableId, state) {
  if (tableId !== NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID) {
    return null;
  }
  return {
    registrationsCompanyFilter: state?.registrationsCompanyFilter ?? null,
    registrationsOrderDateFilter: state?.registrationsOrderDateFilter ?? null,
    registrationsJurisdictionFilter:
      state?.registrationsJurisdictionFilter ?? [],
    registrationsTypeOfRegistrationFilter:
      state?.registrationsTypeOfRegistrationFilter ?? null,
    registrationsSubcategoryFilter:
      state?.registrationsSubcategoryFilter ?? [],
    registrationsStatusFilter: state?.registrationsStatusFilter ?? [],
    registrationsDateLastStatusChangedFilter:
      state?.registrationsDateLastStatusChangedFilter ?? null,
    registrationsRegistratorFilter:
      state?.registrationsRegistratorFilter ?? [],
    registrationsEstimatedRegistrationDateFilter:
      state?.registrationsEstimatedRegistrationDateFilter ?? null,
    registrationsOrdering: state?.registrationsOrdering ?? null,
  };
}

export function normalizeIntermediaryPaymentsActionRequiredFilter(value) {
  const allowed = Object.values(
    NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER
  );
  if (allowed.includes(value)) {
    return value;
  }
  return NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER.ACTION_REQUIRED;
}

export function normalizeIntermediaryPaymentsIsReadFilter(value) {
  const allowed = Object.values(
    NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER
  );
  if (allowed.includes(value)) {
    return value;
  }
  return NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER.ALL;
}

function resolveIntermediaryPaymentsFilters(tableId, state) {
  if (tableId !== NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID) {
    return null;
  }
  return {
    intermediaryPaymentsActionRequiredFilter:
      state?.intermediaryPaymentsActionRequiredFilter ??
      NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER.ACTION_REQUIRED,
    intermediaryPaymentsIsReadFilter:
      state?.intermediaryPaymentsIsReadFilter ??
      NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER.ALL,
    intermediaryPaymentsPriorityFilter: state?.intermediaryPaymentsPriorityFilter ?? [],
    intermediaryPaymentsTaskStateFilter: state?.intermediaryPaymentsTaskStateFilter ?? [],
    intermediaryPaymentsStatusFilter: state?.intermediaryPaymentsStatusFilter ?? [],
    intermediaryPaymentsCompanyFilter: state?.intermediaryPaymentsCompanyFilter ?? null,
  };
}

export function getNotificationCenterIntermediaryPaymentsFilterSignature(
  filters
) {
  if (!filters) {
    return "none";
  }
  const parts = [
    filters.intermediaryPaymentsActionRequiredFilter,
    filters.intermediaryPaymentsIsReadFilter,
    JSON.stringify(filters.intermediaryPaymentsPriorityFilter ?? []),
    JSON.stringify(filters.intermediaryPaymentsTaskStateFilter ?? []),
    JSON.stringify(filters.intermediaryPaymentsStatusFilter ?? []),
    filters.intermediaryPaymentsCompanyFilter ?? "",
  ];
  return parts.join("|");
}

export function getNotificationCenterIntermediaryPaymentsQueryParams(filters) {
  if (!filters) {
    return {};
  }

  const params = {};
  const actionRequired = normalizeIntermediaryPaymentsActionRequiredFilter(
    filters.intermediaryPaymentsActionRequiredFilter
  );
  if (
    actionRequired ===
    NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER.ACTION_REQUIRED
  ) {
    params.action_required = true;
  } else if (
    actionRequired ===
    NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER.HISTORY
  ) {
    params.action_required = false;
  }

  const isRead = normalizeIntermediaryPaymentsIsReadFilter(
    filters.intermediaryPaymentsIsReadFilter
  );
  if (
    isRead === NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER.UNREAD
  ) {
    params.is_read = false;
  }

  const priority = filters.intermediaryPaymentsPriorityFilter;
  if (Array.isArray(priority) && priority.length > 0) {
    params.priority = priority.map(String).filter(Boolean);
  }

  const taskState = filters.intermediaryPaymentsTaskStateFilter;
  if (Array.isArray(taskState) && taskState.length > 0) {
    params.task_state = taskState.map(String).filter(Boolean);
  }

  const status = filters.intermediaryPaymentsStatusFilter;
  if (Array.isArray(status) && status.length > 0) {
    params.status = status.map(String).filter(Boolean);
  }

  const companyId = filters.intermediaryPaymentsCompanyFilter;
  if (companyId != null && String(companyId).trim() !== "") {
    params.company_id = String(companyId).trim();
  }

  return params;
}

function trackNotificationCenterTableRequestPending(state, tableId, requestId, force) {
  if (!tableId || requestId == null) {
    return;
  }
  if (!state.dataByTab[tableId]) {
    state.dataByTab[tableId] = createEmptyTabTableState();
  }
  const tabState = state.dataByTab[tableId];
  const pending = Array.isArray(tabState.pendingRequestIds)
    ? [...tabState.pendingRequestIds]
    : [];
  if (!pending.includes(requestId)) {
    pending.push(requestId);
  }
  tabState.pendingRequestIds = pending;
  if (force) {
    tabState.supersededRequestIds = pending.filter((id) => id !== requestId);
  }
}

function finalizeNotificationCenterTableRequest(state, tableId, requestId) {
  if (!tableId || requestId == null || !state.dataByTab[tableId]) {
    return { isSuperseded: false, pendingRequestIds: [], supersededRequestIds: [] };
  }
  const tabState = state.dataByTab[tableId];
  const pendingRequestIds = (tabState.pendingRequestIds ?? []).filter(
    (id) => id !== requestId
  );
  const supersededRequestIds = (tabState.supersededRequestIds ?? []).filter(
    (id) => id !== requestId
  );
  const isSuperseded = (tabState.supersededRequestIds ?? []).includes(requestId);
  tabState.pendingRequestIds = pendingRequestIds;
  tabState.supersededRequestIds = supersededRequestIds;
  return { isSuperseded, pendingRequestIds, supersededRequestIds };
}

function trackNotificationCenterCountersRequestPending(state, requestId, force) {
  if (requestId == null) {
    return;
  }
  const pending = Array.isArray(state.countersPendingRequestIds)
    ? [...state.countersPendingRequestIds]
    : [];
  if (!pending.includes(requestId)) {
    pending.push(requestId);
  }
  state.countersPendingRequestIds = pending;
  if (force) {
    state.countersSupersededRequestIds = pending.filter((id) => id !== requestId);
  }
}

function finalizeNotificationCenterCountersRequest(state, requestId) {
  if (requestId == null) {
    return { isSuperseded: false, pendingRequestIds: [], supersededRequestIds: [] };
  }
  const pendingRequestIds = (state.countersPendingRequestIds ?? []).filter(
    (id) => id !== requestId
  );
  const supersededRequestIds = (state.countersSupersededRequestIds ?? []).filter(
    (id) => id !== requestId
  );
  const isSuperseded = (state.countersSupersededRequestIds ?? []).includes(requestId);
  state.countersPendingRequestIds = pendingRequestIds;
  state.countersSupersededRequestIds = supersededRequestIds;
  return { isSuperseded, pendingRequestIds, supersededRequestIds };
}

/** Prevents duplicate in-flight GETs for the same table page (e.g. Strict Mode / double effect). */
const inFlightTableRequests = new Set();
const inFlightDeadlinesRequests = new Set();

function resolveDeadlinesFilter(deadlineFilter, stateFilter) {
  const allowed = Object.values(NOTIFICATION_CENTER_DEADLINES_SCOPE);
  if (allowed.includes(deadlineFilter)) {
    return deadlineFilter;
  }
  if (allowed.includes(stateFilter)) {
    return stateFilter;
  }
  return NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS;
}

function deadlinesRequestKey(
  showAll = false,
  deadlineFilter,
  offset = 0,
  limit = NOTIFICATION_CENTER_PAGE_SIZE
) {
  const filter = resolveDeadlinesFilter(
    deadlineFilter,
    NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS
  );
  return `${NOTIFICATION_CENTER_DEADLINES_TAB_ID}:${showAll ? 1 : 0}:${filter}:${offset}:${limit}`;
}

/**
 * GET /notify/notification_center/deadlines?deadline_filter=&offset=&limit=
 * Standard table shape `{ items, total, offset, limit }`; legacy `{ deadlines }` fallback.
 */
export function normalizeNotificationCenterDeadlinesResponse(
  raw,
  fallbackOffset = 0,
  fallbackLimit = NOTIFICATION_CENTER_PAGE_SIZE
) {
  const source =
    raw?.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  if (
    Array.isArray(source?.items) ||
    source?.total != null ||
    source?.offset != null ||
    source?.limit != null
  ) {
    return normalizeNotificationCenterTableResponse(
      raw,
      NOTIFICATION_CENTER_DEADLINES_TAB_ID,
      fallbackOffset,
      fallbackLimit
    );
  }

  const items = normalizeList(source?.deadlines);
  return {
    tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
    items,
    total: items.length,
    offset: Number(fallbackOffset) || 0,
    limit: Number(fallbackLimit) || NOTIFICATION_CENTER_PAGE_SIZE,
  };
}

export const fetchNotificationCenterDeadlines = createAsyncThunk(
  "notificationCenter/fetchDeadlines",
  async (
    {
      silent = false,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      showAll = false,
      deadlineFilter,
    } = {},
    { rejectWithValue, getState }
  ) => {
    const resolvedDeadlineFilter = resolveDeadlinesFilter(
      deadlineFilter,
      getState().notificationCenter?.deadlinesScopeFilter
    );
    const safeOffset = Math.max(0, Number(offset) || 0);
    const safeLimit = Math.max(1, Number(limit) || NOTIFICATION_CENTER_PAGE_SIZE);

    try {
      const response = await getNotificationCenterTable(
        NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        {
          deadline_filter: resolvedDeadlineFilter,
          offset: safeOffset,
          limit: safeLimit,
          ...getNotificationCenterShowAllQueryParams({
            showAll,
            tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }

      const data = await response.json();
      return {
        ...normalizeNotificationCenterDeadlinesResponse(data, safeOffset, safeLimit),
        silent,
        deadlineFilter: resolvedDeadlineFilter,
        showAll,
      };
    } catch (e) {
      return rejectWithValue({
        tableId: NOTIFICATION_CENTER_DEADLINES_TAB_ID,
        message: e?.message || "Error",
        deadlineFilter: resolvedDeadlineFilter,
        showAll,
        offset: safeOffset,
        limit: safeLimit,
      });
    }
  },
  {
    condition: (
      {
        showAll = false,
        deadlineFilter,
        offset = 0,
        limit = NOTIFICATION_CENTER_PAGE_SIZE,
      } = {},
      { getState }
    ) => {
      const resolvedDeadlineFilter = resolveDeadlinesFilter(
        deadlineFilter,
        getState().notificationCenter?.deadlinesScopeFilter
      );
      const key = deadlinesRequestKey(
        showAll,
        resolvedDeadlineFilter,
        Math.max(0, Number(offset) || 0),
        Math.max(1, Number(limit) || NOTIFICATION_CENTER_PAGE_SIZE)
      );
      if (inFlightDeadlinesRequests.has(key)) {
        return false;
      }
      inFlightDeadlinesRequests.add(key);
      return true;
    },
  }
);

export const fetchNotificationCenterTable = createAsyncThunk(
  "notificationCenter/fetchTable",
  async (
    {
      tableId,
      offset = 0,
      limit = NOTIFICATION_CENTER_PAGE_SIZE,
      silent = false,
      showAll = false,
      declarationType,
    } = {},
    { rejectWithValue, getState }
  ) => {
    const resolvedDeclarationType =
      tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
        ? resolveDeclarationTypeFilter(
            declarationType,
            getState().notificationCenter?.declarationTypeFilter
          )
        : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
    const registrationsFilters = resolveRegistrationsFilters(
      tableId,
      getState().notificationCenter
    );
    const registrationsFilterSignature =
      getNotificationCenterRegistrationsFilterSignature(registrationsFilters);
    const intermediaryPaymentsFilters = resolveIntermediaryPaymentsFilters(
      tableId,
      getState().notificationCenter
    );
    const intermediaryPaymentsFilterSignature =
      getNotificationCenterIntermediaryPaymentsFilterSignature(
        intermediaryPaymentsFilters
      );
    const resolvedShowAll =
      tableId === NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID
        ? false
        : showAll;

    try {
      if (!tableId) {
        throw new Error("tableId is required");
      }
      if (!isNotificationCenterPaginatedTable(tableId)) {
        throw new Error(`tableId "${tableId}" does not support pagination`);
      }

      const response = await getNotificationCenterTable(tableId, {
        offset,
        limit,
        ...getNotificationCenterShowAllQueryParams({
          showAll: resolvedShowAll,
          tableId,
        }),
        ...getNotificationCenterDeclarationTypeQueryParams(
          tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
            ? resolvedDeclarationType
            : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL
        ),
        ...getNotificationCenterRegistrationsQueryParams(registrationsFilters),
        ...getNotificationCenterIntermediaryPaymentsQueryParams(
          intermediaryPaymentsFilters
        ),
      });
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }

      const data = await response.json();
      return {
        ...normalizeNotificationCenterTableResponse(data, tableId, offset, limit),
        silent,
        declarationType: resolvedDeclarationType,
        showAll: resolvedShowAll,
        registrationsFilterSignature,
        intermediaryPaymentsFilterSignature,
      };
    } catch (e) {
      return rejectWithValue({
        tableId,
        message: e?.message || "Error",
        declarationType: resolvedDeclarationType,
        showAll: resolvedShowAll,
        offset,
        limit,
        registrationsFilterSignature,
        intermediaryPaymentsFilterSignature,
      });
    }
  },
  {
    condition: (
      {
        tableId,
        offset = 0,
        limit = NOTIFICATION_CENTER_PAGE_SIZE,
        showAll = false,
        declarationType,
        force = false,
      } = {},
      { getState }
    ) => {
      if (!tableId || !isNotificationCenterPaginatedTable(tableId)) {
        return false;
      }
      const resolvedDeclarationType =
        tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
          ? resolveDeclarationTypeFilter(
              declarationType,
              getState().notificationCenter?.declarationTypeFilter
            )
          : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
      const resolvedShowAll =
        tableId === NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID
          ? false
          : showAll;
      const key = tableRequestKey(
        tableId,
        offset,
        limit,
        resolvedShowAll,
        resolvedDeclarationType,
        getNotificationCenterRegistrationsFilterSignature(
          resolveRegistrationsFilters(tableId, getState().notificationCenter)
        ),
        getNotificationCenterIntermediaryPaymentsFilterSignature(
          resolveIntermediaryPaymentsFilters(
            tableId,
            getState().notificationCenter
          )
        )
      );
      if (inFlightTableRequests.has(key) && !force) {
        return false;
      }
      inFlightTableRequests.add(key);
      return true;
    },
  }
);

export const fetchNotificationCenterCounters = createAsyncThunk(
  "notificationCenter/fetchCounters",
  async ({ silent = false, showAll = false } = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenterCounters(
        getNotificationCenterShowAllQueryParams({ showAll })
      );
      if (!response.ok) {
        throw new Error(response.errorMessage || "Error");
      }
      const data = await response.json();
      return normalizeNotificationCenterCounters(data);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Error"
      );
    }
  }
);

export const markNotificationCenterIntermediaryPaymentsReadThunk =
  createAsyncThunk(
    "notificationCenter/markIntermediaryPaymentsRead",
    async (body = {}, { rejectWithValue }) => {
      try {
        const response = await markNotificationCenterIntermediaryPaymentsRead(
          body
        );
        if (!response.ok) {
          throw new Error(response.errorMessage || "Error");
        }
        const data = await response.json();
        const source =
          data?.data && typeof data.data === "object" && !Array.isArray(data.data)
            ? data.data
            : data;
        return {
          ids: Array.isArray(body?.ids) ? body.ids : [],
          all: body?.all === true,
          updated: Number(source?.updated) || 0,
          unreadCount: Number(source?.unread_count ?? source?.unreadCount) || 0,
        };
      } catch (e) {
        return rejectWithValue(e?.message || "Error");
      }
    }
  );

/** Reload active table page and bell/summary counters after read, close, etc. */
export const refreshNotificationCenterAfterAction = createAsyncThunk(
  "notificationCenter/refreshAfterAction",
  async (arg = {}, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState().notificationCenter;
      const tableId = arg.tableId ?? state.lastFetchedTableId;
      const tabSection = tableId ? state.dataByTab[tableId]?.default : null;
      const user = getState().user?.user;
      const showAll = resolveNotificationCenterShowAllForApi({
        visibilityFilter: state.visibilityFilter,
        tableId,
        user,
      });
      const force = Boolean(arg.force);

      const tasks = [
        dispatch(
          fetchNotificationCenterCounters({ silent: true, showAll, force })
        ).unwrap(),
      ];

      if (tableId === NOTIFICATION_CENTER_DEADLINES_TAB_ID) {
        tasks.push(
          dispatch(
            fetchNotificationCenterDeadlines({
              silent: true,
              showAll,
              offset: arg.offset ?? tabSection?.offset ?? 0,
              limit: arg.limit ?? tabSection?.limit ?? NOTIFICATION_CENTER_PAGE_SIZE,
            })
          ).unwrap()
        );
      } else if (tableId) {
        tasks.push(
          dispatch(
            fetchNotificationCenterTable({
              tableId,
              offset: arg.offset ?? tabSection?.offset ?? 0,
              limit: arg.limit ?? tabSection?.limit ?? NOTIFICATION_CENTER_PAGE_SIZE,
              silent: true,
              showAll,
              force,
              declarationType:
                tableId === NOTIFICATION_CENTER_REPORTING_TAB_ID
                  ? state.declarationTypeFilter
                  : undefined,
            })
          ).unwrap()
        );
      }

      await Promise.all(tasks);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Error"
      );
    }
  }
);

function getNotificationCenterInitialState() {
  const storedFilters = loadNotificationCenterFiltersFromStorage();

  return {
    dataByTab: createEmptyByTab(),
    lastFetchedTableId: null,
    counters: normalizeNotificationCenterCounters(null),
    countersLoading: false,
    countersError: null,
    countersHydrated: false,
    visibilityFilter: storedFilters.visibilityFilter,
    deadlinesScopeFilter: storedFilters.deadlinesScopeFilter,
    declarationTypeFilter: storedFilters.declarationTypeFilter,
    registrationsCompanyFilter: null,
    registrationsOrderDateFilter: null,
    registrationsJurisdictionFilter: [],
    registrationsTypeOfRegistrationFilter: null,
    registrationsSubcategoryFilter: [],
    registrationsStatusFilter: getNotificationCenterRegistrationsDefaultStatuses(),
    registrationsDateLastStatusChangedFilter: null,
    registrationsRegistratorFilter: [],
    registrationsEstimatedRegistrationDateFilter: null,
    registrationsOrdering: null,
    registrationsFilterOptions: createEmptyRegistrationsFilterOptions(),
    registrationsHighlightError: null,
    intermediaryPaymentsActionRequiredFilter:
      NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_ACTION_REQUIRED_FILTER.ACTION_REQUIRED,
    intermediaryPaymentsIsReadFilter:
      NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_IS_READ_FILTER.ALL,
    intermediaryPaymentsPriorityFilter: [],
    intermediaryPaymentsTaskStateFilter: [],
    intermediaryPaymentsStatusFilter: [],
    intermediaryPaymentsCompanyFilter: null,
    countersPendingRequestIds: [],
    countersSupersededRequestIds: [],
  };
}

const initialState = getNotificationCenterInitialState();

const notificationCenterSlice = createSlice({
  name: "notificationCenter",
  initialState,
  reducers: {
    rehydrateNotificationCenterFiltersFromStorage: (state) => {
      applyStoredNotificationCenterFiltersToState(state);
    },
    setNotificationCenterFilter: (state, action) => {
      const { reduxKey, value } = action.payload ?? {};
      if (!reduxKey) {
        return;
      }

      if (reduxKey === "visibilityFilter") {
        state.visibilityFilter =
          value === NOTIFICATION_CENTER_VISIBILITY.ALL
            ? NOTIFICATION_CENTER_VISIBILITY.ALL
            : NOTIFICATION_CENTER_VISIBILITY.MINE;
      } else if (reduxKey === "deadlinesScopeFilter") {
        const allowed = Object.values(NOTIFICATION_CENTER_DEADLINES_SCOPE);
        state.deadlinesScopeFilter = allowed.includes(value)
          ? value
          : NOTIFICATION_CENTER_DEADLINES_SCOPE.NEXT_3_DAYS;
      } else if (reduxKey === "declarationTypeFilter") {
        const allowed = Object.values(NOTIFICATION_CENTER_DECLARATION_TYPE);
        state.declarationTypeFilter = allowed.includes(value)
          ? value
          : NOTIFICATION_CENTER_DECLARATION_TYPE.ALL;
      } else if (reduxKey === "registrationsCompanyFilter") {
        state.registrationsCompanyFilter =
          normalizeRegistrationsCompanyFilter(value);
      } else if (reduxKey === "registrationsOrderDateFilter") {
        state.registrationsOrderDateFilter =
          normalizeRegistrationsOrderDateFilter(value);
      } else if (reduxKey === "registrationsJurisdictionFilter") {
        state.registrationsJurisdictionFilter =
          normalizeRegistrationsJurisdictionFilter(value);
      } else if (reduxKey === "registrationsTypeOfRegistrationFilter") {
        state.registrationsTypeOfRegistrationFilter =
          normalizeRegistrationsTypeFilter(value);
        if (
          String(state.registrationsTypeOfRegistrationFilter).toUpperCase() !==
          "EPR"
        ) {
          state.registrationsSubcategoryFilter = [];
        }
      } else if (reduxKey === "registrationsSubcategoryFilter") {
        state.registrationsSubcategoryFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (reduxKey === "registrationsStatusFilter") {
        state.registrationsStatusFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (
        reduxKey === "registrationsDateLastStatusChangedFilter"
      ) {
        state.registrationsDateLastStatusChangedFilter =
          normalizeRegistrationsDateFilter(value);
      } else if (reduxKey === "registrationsRegistratorFilter") {
        state.registrationsRegistratorFilter =
          normalizeRegistrationsMultiSelectFilter(value);
      } else if (
        reduxKey === "registrationsEstimatedRegistrationDateFilter"
      ) {
        state.registrationsEstimatedRegistrationDateFilter =
          normalizeRegistrationsDateFilter(value);
      } else if (reduxKey === "registrationsOrdering") {
        state.registrationsOrdering = normalizeRegistrationsOrdering(value);
      } else if (reduxKey === "intermediaryPaymentsActionRequiredFilter") {
        state.intermediaryPaymentsActionRequiredFilter =
          normalizeIntermediaryPaymentsActionRequiredFilter(value);
      } else if (reduxKey === "intermediaryPaymentsIsReadFilter") {
        state.intermediaryPaymentsIsReadFilter =
          normalizeIntermediaryPaymentsIsReadFilter(value);
      } else if (reduxKey === "intermediaryPaymentsPriorityFilter") {
        state.intermediaryPaymentsPriorityFilter = Array.isArray(value)
          ? value.map(String).filter(Boolean)
          : [];
      } else if (reduxKey === "intermediaryPaymentsTaskStateFilter") {
        state.intermediaryPaymentsTaskStateFilter = Array.isArray(value)
          ? value.map(String).filter(Boolean)
          : [];
      } else if (reduxKey === "intermediaryPaymentsStatusFilter") {
        state.intermediaryPaymentsStatusFilter = Array.isArray(value)
          ? value.map(String).filter(Boolean)
          : [];
      } else if (reduxKey === "intermediaryPaymentsCompanyFilter") {
        state.intermediaryPaymentsCompanyFilter =
          value == null || String(value).trim() === "" ? null : String(value).trim();
      }

      saveNotificationCenterFiltersToStorage(state);
    },
    clearNotificationCenterError: (state, action) => {
      const tableId = action.payload?.tableId ?? state.lastFetchedTableId;
      if (tableId && state.dataByTab[tableId]) {
        state.dataByTab[tableId].error = null;
        return;
      }
      for (const tabId of Object.keys(state.dataByTab)) {
        if (state.dataByTab[tabId]) {
          state.dataByTab[tabId].error = null;
        }
      }
    },
    clearNotificationCenterHighlightError: (state) => {
      state.registrationsHighlightError = null;
    },
    beginNotificationCenterTabLoad: (state, action) => {
      const tableId = action.payload?.tableId;
      if (!tableId) return;
      if (!state.dataByTab[tableId]) {
        state.dataByTab[tableId] = createEmptyTabTableState(action.payload?.limit);
      }
      state.dataByTab[tableId].loading = true;
      state.dataByTab[tableId].error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCenterTable.pending, (state, action) => {
        const { tableId, limit, force = false } = action.meta.arg ?? {};
        if (!tableId) return;

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState(limit);
        }

        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        if (!action.meta?.arg?.background) {
          state.lastFetchedTableId = tableId;
        }
        trackNotificationCenterTableRequestPending(
          state,
          tableId,
          action.meta.requestId,
          Boolean(force)
        );
      })
      .addCase(fetchNotificationCenterTable.fulfilled, (state, action) => {
        const { tableId, items, total, offset, limit } = action.payload;
        const showAll = Boolean(action.payload?.showAll ?? action.meta?.arg?.showAll);
        const declarationType =
          action.payload?.declarationType ?? action.meta?.arg?.declarationType;
        inFlightTableRequests.delete(
          tableRequestKey(
            tableId,
            offset,
            limit,
            showAll,
            declarationType,
            action.payload?.registrationsFilterSignature,
            action.payload?.intermediaryPaymentsFilterSignature
          )
        );
        const { isSuperseded, pendingRequestIds, supersededRequestIds } =
          finalizeNotificationCenterTableRequest(state, tableId, action.meta.requestId);

        if (tableId === NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID && !isSuperseded) {
          state.registrationsFilterOptions =
            collectNotificationCenterRegistrationsFilterOptions(
              items,
              state.registrationsFilterOptions
            );
        }

        if (isSuperseded) {
          if (state.dataByTab[tableId]) {
            state.dataByTab[tableId].pendingRequestIds = pendingRequestIds;
            state.dataByTab[tableId].supersededRequestIds = supersededRequestIds;
          }
          return;
        }

        state.dataByTab[tableId] = {
          loading: false,
          loaded: true,
          error: null,
          pendingRequestIds,
          supersededRequestIds,
          fetchedRequestId: action.meta.requestId,
          default: {
            items,
            total,
            offset,
            limit,
          },
        };
        if (!action.meta?.arg?.background) {
          state.lastFetchedTableId = tableId;
        }
      })
      .addCase(fetchNotificationCenterDeadlines.pending, (state, action) => {
        const tableId = NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        const { limit } = action.meta.arg ?? {};
        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState(limit);
        }
        if (!isSilentFetch(action.meta?.arg)) {
          state.dataByTab[tableId].loading = true;
        }
        state.dataByTab[tableId].error = null;
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.fulfilled, (state, action) => {
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          deadlineFilter,
        } = action.meta?.arg ?? {};
        inFlightDeadlinesRequests.delete(
          deadlinesRequestKey(
            Boolean(action.payload?.showAll ?? showAll),
            action.payload?.deadlineFilter ?? deadlineFilter,
            offset,
            limit
          )
        );
        const {
          tableId,
          items,
          total,
          offset: responseOffset,
          limit: responseLimit,
        } = action.payload;

        state.dataByTab[tableId] = {
          loading: false,
          loaded: true,
          error: null,
          default: {
            items,
            total,
            offset: responseOffset,
            limit: responseLimit,
          },
        };
        state.lastFetchedTableId = tableId;
      })
      .addCase(fetchNotificationCenterDeadlines.rejected, (state, action) => {
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          deadlineFilter,
        } = action.meta?.arg ?? {};
        inFlightDeadlinesRequests.delete(
          deadlinesRequestKey(
            Boolean(action.payload?.showAll ?? showAll),
            action.payload?.deadlineFilter ?? deadlineFilter,
            action.payload?.offset ?? offset,
            action.payload?.limit ?? limit
          )
        );
        const tableId =
          action.payload?.tableId ?? NOTIFICATION_CENTER_DEADLINES_TAB_ID;
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Error";

        if (!state.dataByTab[tableId]) {
          state.dataByTab[tableId] = createEmptyTabTableState();
        }
        state.dataByTab[tableId].loading = false;
        state.dataByTab[tableId].loaded = true;
        state.dataByTab[tableId].error = message;
      })
      .addCase(fetchNotificationCenterTable.rejected, (state, action) => {
        const tableId = action.payload?.tableId ?? action.meta?.arg?.tableId;
        const {
          offset = 0,
          limit = NOTIFICATION_CENTER_PAGE_SIZE,
          showAll = false,
          declarationType,
        } = action.meta?.arg ?? {};
        if (tableId) {
          inFlightTableRequests.delete(
            tableRequestKey(
              tableId,
              action.payload?.offset ?? offset,
              action.payload?.limit ?? limit,
              Boolean(action.payload?.showAll ?? showAll),
              action.payload?.declarationType ?? declarationType,
              action.payload?.registrationsFilterSignature,
              action.payload?.intermediaryPaymentsFilterSignature
            )
          );
          finalizeNotificationCenterTableRequest(state, tableId, action.meta.requestId);
        }
        const message =
          action.payload?.message ||
          action.error?.message ||
          "Error";

        if (tableId) {
          if (!state.dataByTab[tableId]) {
            state.dataByTab[tableId] = createEmptyTabTableState();
          }
          state.dataByTab[tableId].loading = false;
          state.dataByTab[tableId].loaded = true;
          state.dataByTab[tableId].error = message;
        }
      })
      .addCase(fetchNotificationCenterCounters.pending, (state, action) => {
        if (!isSilentFetch(action.meta?.arg)) {
          state.countersLoading = true;
        }
        state.countersError = null;
        trackNotificationCenterCountersRequestPending(
          state,
          action.meta.requestId,
          Boolean(action.meta?.arg?.force)
        );
      })
      .addCase(fetchNotificationCenterCounters.fulfilled, (state, action) => {
        const { isSuperseded, pendingRequestIds, supersededRequestIds } =
          finalizeNotificationCenterCountersRequest(state, action.meta.requestId);
        state.countersLoading = false;
        if (isSuperseded) {
          state.countersPendingRequestIds = pendingRequestIds;
          state.countersSupersededRequestIds = supersededRequestIds;
          return;
        }
        state.counters = action.payload ?? normalizeNotificationCenterCounters(null);
        state.countersHydrated = true;
        state.countersPendingRequestIds = pendingRequestIds;
        state.countersSupersededRequestIds = supersededRequestIds;
      })
      .addCase(fetchNotificationCenterCounters.rejected, (state, action) => {
        finalizeNotificationCenterCountersRequest(state, action.meta.requestId);
        state.countersLoading = false;
        state.countersError =
          action.payload || action.error?.message || "Error";
      })
      .addCase(
        markNotificationCenterIntermediaryPaymentsReadThunk.fulfilled,
        (state, action) => {
          const { ids = [], all, unreadCount } = action.payload ?? {};
          if (!state.counters?.intermediaryPayments) {
            state.counters.intermediaryPayments =
              createEmptyIntermediaryPaymentsCounters();
          }
          state.counters.intermediaryPayments.unreadCount = unreadCount;

          const section =
            state.dataByTab[NOTIFICATION_CENTER_INTERMEDIARY_PAYMENTS_TAB_ID]
              ?.default;
          if (!Array.isArray(section?.items)) {
            return;
          }
          const idSet = all ? null : new Set(ids.map((id) => Number(id)));
          const nowIso = new Date().toISOString();
          section.items = section.items.map((item) => {
            if (all || (idSet && idSet.has(Number(item?.id)))) {
              return { ...item, read_at: item.read_at ?? nowIso };
            }
            return item;
          });
        }
      )
      .addCase(updateRegistrationTrackerHighlighted.pending, (state) => {
        state.registrationsHighlightError = null;
      })
      // The flag lives on the tracker record, so keep the Registrations rows in
      // sync with the toggle instead of refetching the whole table.
      .addCase(updateRegistrationTrackerHighlighted.fulfilled, (state, action) => {
        const { trackerId, highlighted } = action.payload ?? {};
        const section =
          state.dataByTab[NOTIFICATION_CENTER_REGISTRATIONS_TAB_ID]?.default;
        if (!trackerId || !Array.isArray(section?.items)) {
          return;
        }
        section.items = section.items.map((item) =>
          getNotificationCenterRegistrationTrackerId(item) === String(trackerId)
            ? { ...item, highlighted: highlighted === true }
            : item
        );
      })
      .addCase(updateRegistrationTrackerHighlighted.rejected, (state, action) => {
        state.registrationsHighlightError =
          action.payload ||
          action.error?.message ||
          "Failed to update the attention flag";
      })
      .addCase(login.fulfilled, (state) => {
        applyStoredNotificationCenterFiltersToState(state);
      });
  },
});

export const {
  beginNotificationCenterTabLoad,
  clearNotificationCenterError,
  clearNotificationCenterHighlightError,
  setNotificationCenterFilter,
  rehydrateNotificationCenterFiltersFromStorage,
} = notificationCenterSlice.actions;

const notificationCenterReducer = (state, action) => {
  if (state === undefined) {
    return notificationCenterSlice.reducer(getNotificationCenterInitialState(), action);
  }
  return notificationCenterSlice.reducer(state, action);
};

export default notificationCenterReducer;
